import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { BRAND, FOOTER_LINKS, ASSETS } from "@/content/site";
import { subscribeNewsletter } from "@/lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | done | error

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState("loading");
    try {
      await subscribeNewsletter(email);
      setState("done");
      setEmail("");
    } catch {
      setState("error");
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-navy text-white" style={{ backgroundColor: "var(--navy)" }}>
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-16">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1.4fr]">
          {/* Brand */}
          <div>
            <img src={ASSETS.logo} alt="سياق" width="140" height="52" className="h-12 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
            <p className="mt-5 text-white/70 leading-relaxed max-w-xs">حلولنا الاستشارية نبتكرها وفقًا للسياق</p>
            <a href={`mailto:${BRAND.email}`} className="mt-5 inline-block text-peach link-underline" data-testid="footer-email">
              {BRAND.email}
            </a>
          </div>

          {/* Links */}
          <nav aria-label="روابط سياق">
            <h3 className="text-sm font-semibold text-peach mb-4">الروابط الرئيسية</h3>
            <ul className="space-y-1">
              {FOOTER_LINKS.siaq.map((l) => (
                <li key={l.href}>
                  <Link to={l.href} className="block py-2.5 -my-0.5 text-white/75 hover:text-white transition-colors" data-testid={`footer-link-${l.href.replace("/", "") || "home"}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="روابط إضافية">
            <h3 className="text-sm font-semibold text-peach mb-4">المزيد</h3>
            <ul className="space-y-1">
              {FOOTER_LINKS.more.map((l) => (
                <li key={l.href}>
                  <Link to={l.href} className="block py-2.5 -my-0.5 text-white/75 hover:text-white transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Newsletter */}
          <div>
            <h3 className="text-sm font-semibold text-peach mb-4">النشرة المعرفية</h3>
            <p className="text-white/70 mb-4 text-sm leading-relaxed">اشترك لتصلك إصداراتنا ومقالاتنا الجديدة.</p>
            <form onSubmit={onSubscribe} className="flex items-stretch border border-white/20" data-testid="newsletter-form">
              <label htmlFor="footer-newsletter" className="sr-only">البريد الإلكتروني</label>
              <input
                id="footer-newsletter"
                type="email"
                required
                value={email}
                onChange={(e) => { setEmail(e.target.value); setState("idle"); }}
                placeholder="بريدك الإلكتروني"
                className="flex-1 bg-transparent px-4 py-3 text-white placeholder:text-white/40 outline-none"
                data-testid="newsletter-input"
              />
              <button type="submit" disabled={state === "loading"} className="bg-coral px-4 flex items-center" style={{ backgroundColor: "var(--coral)" }} aria-label="اشتراك" data-testid="newsletter-submit">
                {state === "done" ? <Check size={18} /> : <ArrowLeft size={18} />}
              </button>
            </form>
            {state === "done" && <p className="mt-3 text-sm text-peach" role="status">تم اشتراكك، شكرًا لك.</p>}
            {state === "error" && <p className="mt-3 text-sm text-coral" role="alert">تعذّر الاشتراك، حاول مجددًا.</p>}

            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-1">
              {BRAND.socials.map((s) => (
                <a key={s.name} href={s.href} target="_blank" rel="noopener noreferrer" className="inline-block py-2 text-white/60 hover:text-peach transition-colors text-sm" aria-label={s.label}>
                  {s.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-3 text-sm text-white/50">
          <p>© {new Date().getFullYear()} سياق للاستشارات وحلول تمكين القدرات. جميع الحقوق محفوظة.</p>
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <Link to="/privacy" className="inline-block py-2 hover:text-white">سياسة الخصوصية</Link>
            <Link to="/terms" className="inline-block py-2 hover:text-white">الشروط والأحكام</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
